import { AppBar, Avatar, Box, Button, IconButton, Stack, Toolbar, Tooltip, Typography } from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import { useThemeMode } from "context/ThemeContextProvider";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import BrandLockup from "components/common/BrandLockup";
import PageHelpButton from "components/common/PageHelpButton";
import type { PageInfoContent } from "components/common/PageHelpButton";
import { useUserRoleContext } from "context/NetworkContext";
import { feedbackViewPermissions } from "services/permissions";
import {
  getHeaderAppBarSx,
  getHeaderActionButtonSx,
  getHeaderIconButtonSx,
  getHeaderProfileBadgeSx,
  headerAvatarSx,
  headerProfileNameSx,
  headerToolbarSx,
} from "./headerStyles";

interface HeaderProps {
  profileName: string;
  isFeedbackOpen: boolean;
  isPageInfoOpen: boolean;
  pageInfo?: PageInfoContent;
  onFeedbackOpen: () => void;
  onPageInfoClose: () => void;
  onPageInfoOpen: () => void;
}

function Header({
  profileName,
  isFeedbackOpen,
  isPageInfoOpen,
  pageInfo,
  onFeedbackOpen,
  onPageInfoClose,
  onPageInfoOpen,
}: HeaderProps) {
  const { mode, toggleTheme } = useThemeMode();
  const { userRole } = useUserRoleContext();
  const canShowFeedback = Boolean(userRole && feedbackViewPermissions.includes(userRole));
  const isFeedbackActive = isFeedbackOpen;
  const isPageInfoActive = Boolean(pageInfo && isPageInfoOpen);

  return (
    <AppBar position="fixed" elevation={0} sx={(theme) => getHeaderAppBarSx(theme)}>
      <Toolbar sx={headerToolbarSx}>
        <BrandLockup compact hideTitleOnMobile inverted />

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={mode === "light" ? "Switch to dark mode" : "Switch to light mode"}>
            <IconButton color="inherit" onClick={toggleTheme} sx={(theme) => getHeaderIconButtonSx(theme)}>
              {mode === "light" ? <DarkModeOutlinedIcon /> : <LightModeOutlined />}
            </IconButton>
          </Tooltip>
          {canShowFeedback && (
            <Tooltip title="Share product feedback">
              <Button
                size="small"
                startIcon={<FeedbackOutlinedIcon />}
                onClick={onFeedbackOpen}
                sx={(theme) => getHeaderActionButtonSx(theme, isFeedbackActive)}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Feedback
                </Box>
              </Button>
            </Tooltip>
          )}
          {pageInfo && (
            <PageHelpButton
              open={isPageInfoOpen}
              pageInfo={pageInfo}
              onClose={onPageInfoClose}
              onOpen={onPageInfoOpen}
              sx={(theme) => getHeaderActionButtonSx(theme, isPageInfoActive)}
            />
          )}
          <Tooltip title={profileName || "Signed in user"}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={(theme) => getHeaderProfileBadgeSx(theme)}>
              <Avatar sx={headerAvatarSx}>
                <AccountCircleOutlinedIcon fontSize="small" />
              </Avatar>
              <Typography variant="body2" sx={headerProfileNameSx} noWrap>
                {profileName || "Loading profile"}
              </Typography>
            </Stack>
          </Tooltip>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
